import { useEffect, useState, useCallback, useRef } from 'react'
import { useAppDispatch, useAppSelector } from '../../store'
import { selectUnviewedFeedbackReadyNotifications } from '../../store/selectors/inAppNotificationsSelectors'
import { markViewedLocally } from '../../store/slices/inAppNotificationsSlice'
import { processHandlers } from '../../apiLayer/processHandlers'
import { FeedbackReadyPayload } from 'pure-js/types/InAppNotification'

export interface FeedbackReadyNotificationItem {
  id: string
  sessionId: string
  /** The inAppNotification.id this item was created from, for marking viewed on dismiss */
  inAppNotificationId: string
  timestamp: number
}

let feedbackReadyIdCounter = 0
const generateFeedbackReadyId = () => `feedback-ready-${Date.now()}-${++feedbackReadyIdCounter}`

/**
 * Reads FEEDBACK_READY notifications from the inAppNotificationsSlice (backend-driven)
 * and shows a "Feedback ready" notification for each unviewed entry.
 * One notification per session; marks viewed on dismiss.
 */
export const useFeedbackReadyNotifications = () => {
  const dispatch = useAppDispatch()
  const [notifications, setNotifications] = useState<FeedbackReadyNotificationItem[]>([])
  const shownSessionIdsRef = useRef<Set<string>>(new Set())

  const feedbackReadyInAppNotifications = useAppSelector(selectUnviewedFeedbackReadyNotifications)

  // When new FEEDBACK_READY notifications appear, surface them as local toast items
  useEffect(() => {
    const newItems: FeedbackReadyNotificationItem[] = []

    for (const n of feedbackReadyInAppNotifications) {
      const payload = n.payload as FeedbackReadyPayload
      const sessionId = payload.sessionId

      // One notification per session — deduplicate by sessionId
      if (shownSessionIdsRef.current.has(sessionId)) continue
      shownSessionIdsRef.current.add(sessionId)

      newItems.push({
        id: generateFeedbackReadyId(),
        sessionId,
        inAppNotificationId: n.id,
        timestamp: Date.now()
      })
    }

    if (newItems.length > 0) {
      setNotifications((prev) => [...prev, ...newItems])
    }
  }, [feedbackReadyInAppNotifications])

  // Pure dismiss — no mark-viewed (for X button and auto-close)
  const dismissFeedbackReady = useCallback((id: string) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id))
  }, [])

  // View + dismiss — side effects BEFORE setState (not inside updater!)
  const viewFeedbackReady = useCallback(
    (id: string) => {
      const item = notifications.find((n) => n.id === id)
      if (item) {
        dispatch(markViewedLocally(item.inAppNotificationId))
        processHandlers.markNotificationViewed(item.inAppNotificationId).catch(() => {
          // Ignore errors — notification is already dismissed locally
        })
      }
      setNotifications((prev) => prev.filter((n) => n.id !== id))
    },
    [dispatch, notifications]
  )

  // Clean up old feedback-ready notifications (older than 5 minutes)
  useEffect(() => {
    const interval = setInterval(() => {
      const fiveMinutesAgo = Date.now() - 5 * 60 * 1000
      setNotifications((prev) => prev.filter((n) => n.timestamp > fiveMinutesAgo))
    }, 60000)
    return () => clearInterval(interval)
  }, [])

  return { feedbackReadyNotifications: notifications, dismissFeedbackReady, viewFeedbackReady }
}
